import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { isFeatureUnlocked } from "../lib/featureAccess";
import Tour, { hasSeenTour } from "../components/Tour";
import FeedbackWidget from "../components/FeedbackWidget";
import InstallPrompt from "../components/InstallPrompt";
import ThemeToggle from "../components/ThemeToggle";
import {
  SquaresFour,
  Buildings,
  Scales,
  Calculator,
  Sparkle,
  SignOut,
  ChartPieSlice,
  Crown,
  ShieldCheck,
  Lightning,
  BookOpen,
  Question,
  Gift,
  UserCircle,
  MapTrifold,
  Scroll,
  List,
  X,
  DotsThreeOutline,
  MapPin,
  Newspaper,
  Bank,
  ClockCounterClockwise,
  Lock,
} from "@phosphor-icons/react";

function daysLeft(iso) {
  if (!iso) return 0;
  const d = new Date(iso);
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { access } = useFeatureAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [showTour, setShowTour] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    // Only auto-open the onboarding tour on the dashboard landing route so it
    // doesn't intercept clicks on sub-pages like /app/tenants or /app/will.
    if (user && !hasSeenTour() && location.pathname === "/app") {
      const t = setTimeout(() => setShowTour(true), 500);
      return () => clearTimeout(t);
    }
  }, [user, location.pathname]);

  // Close the mobile nav on route change
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const baseNav = [
    { to: "/app", label: "Dashboard", icon: SquaresFour, end: true, id: "nav-dashboard", slug: "dashboard" },
    { to: "/app/properties", label: "Properties", icon: Buildings, id: "nav-properties", slug: "properties" },
    { to: "/app/rent-comps", label: "Rent comps", icon: MapPin, id: "nav-rent-comps", slug: "rent-comps" },
    { to: "/app/news", label: "News", icon: Newspaper, id: "nav-news", slug: "news" },
    { to: "/app/portfolio", label: "Portfolio", icon: ChartPieSlice, id: "nav-portfolio", slug: "portfolio" },
    { to: "/app/property-history", label: "Property history", icon: ClockCounterClockwise, id: "nav-property-history", slug: "property-history" },
    { to: "/app/tenants", label: "Tenants", icon: UserCircle, id: "nav-tenants", slug: "tenants" },
    { to: "/app/projects", label: "Projects", icon: MapTrifold, id: "nav-projects", slug: "projects" },
    { to: "/app/will", label: "Will", icon: Scroll, id: "nav-will", slug: "will" },
    { to: "/app/compare", label: "Compare", icon: Scales, id: "nav-compare", slug: "compare" },
    { to: "/app/calculators", label: "Calculators", icon: Calculator, id: "nav-calculators", slug: "calculators" },
    { to: "/app/loans", label: "Loan rebalancer", icon: Bank, id: "nav-loans", slug: "loan-rebalancer" },
    { to: "/app/advisor", label: "AI Advisor", icon: Sparkle, id: "nav-advisor", slug: "advisor" },
    { to: "/app/referrals", label: "Invites", icon: Gift, id: "nav-referrals", slug: "referrals" },
    { to: "/app/guide", label: "Guide", icon: BookOpen, id: "nav-guide", slug: "guide" },
  ];
  const nav = (user?.role === "admin" || user?.role === "node_admin")
    ? [...baseNav, { to: "/app/admin", label: user?.role === "node_admin" ? "Node admin" : "Admin", icon: ShieldCheck, id: "nav-admin" }]
    : baseNav;

  const trialLeft = user?.plan_status === "trial" ? daysLeft(user?.trial_ends_at) : 0;
  const showTrialBanner = user && user.plan_status === "trial" && trialLeft >= 0;
  const showExpiredBanner = user && user.plan_status === "expired" && user.role !== "admin" && user.role !== "node_admin";
  const hideBannerOnAdmin = location.pathname.startsWith("/app/admin");

  // Bottom tab bar — mobile only (5 most-used slots)
  const bottomTabs = [
    { to: "/app", label: "Home", icon: SquaresFour, end: true, id: "dashboard" },
    { to: "/app/calculators", label: "Calc", icon: Calculator, id: "calculators" },
    { to: "/app/compare", label: "Compare", icon: Scales, id: "compare" },
    { to: "/app/advisor", label: "Advisor", icon: Sparkle, id: "advisor" },
    { icon: DotsThreeOutline, isMore: true },
  ];

  const SidebarContent = (
    <>
      <div className="p-6 border-b hairline flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-2">
          <div className="h-8 w-8 border hairline flex items-center justify-center">
            <span className="font-serif text-xl leading-none">E</span>
          </div>
          <span className="font-serif text-2xl tracking-tight">Estima</span>
        </Link>
        <button
          onClick={() => setNavOpen(false)}
          className="lg:hidden text-muted-foreground hover:text-foreground"
          aria-label="Close navigation"
          data-testid="mobile-drawer-close"
        >
          <X size={22} weight="bold" />
        </button>
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {nav.map((n) => {
          const Icon = n.icon;
          const locked = !!n.slug && access && !isFeatureUnlocked(access, n.slug);
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition ${
                  isActive
                    ? "bg-[hsl(var(--muted))] text-foreground border-l-2 border-[hsl(var(--primary))]"
                    : locked
                      ? "text-muted-foreground/60 hover:text-muted-foreground hover:bg-[hsl(var(--muted))]"
                      : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span className="flex-1">{n.label}</span>
              {locked && <Lock size={11} weight="duotone" className="text-muted-foreground/70" />}
            </NavLink>
          );
        })}
      </nav>

      {user && access && access.tier !== "pro" && (
        <div className="p-4 border-t hairline space-y-2">
          <Link
            to="/app/profile"
            className="btn-primary w-full px-3 py-2 text-sm inline-flex items-center justify-center gap-2"
            data-testid="sidebar-upgrade-cta"
          >
            <Crown size={14} weight="duotone" /> {access.tier === "basic" ? "Upgrade to Pro" : "Choose a plan"}
          </Link>
          <Link
            to="/app/profile"
            className="block text-center text-[11px] text-muted-foreground hover:text-foreground"
            data-testid="sidebar-manage-sub"
          >
            Manage subscription
          </Link>
        </div>
      )}

      <div className="p-4 border-t hairline">
        <Link
          to="/app/profile"
          className="block text-xs text-muted-foreground mb-2 truncate hover:text-foreground"
          data-testid="sidebar-user-email"
        >
          <span className="inline-flex items-center gap-1.5">
            {user?.is_pro && <Crown size={11} weight="duotone" className="text-[hsl(var(--secondary))] shrink-0" />}
            <span className="truncate">{user?.email}</span>
          </span>
        </Link>
        <button
          onClick={doLogout}
          className="btn-ghost w-full px-3 py-2 text-sm inline-flex items-center justify-center gap-2"
          data-testid="logout-button"
        >
          <SignOut size={16} /> Sign out
        </button>
        <div className="mt-2">
          <ThemeToggle variant="row" testid="sidebar-theme-toggle" />
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <Tour open={showTour} onClose={() => setShowTour(false)} />

      {/* Desktop sidebar (lg+) — sticky so it stays visible while the right pane scrolls */}
      <aside className="hidden lg:flex w-64 border-r hairline flex-col shrink-0 sticky top-0 h-screen self-start" data-testid="desktop-sidebar">
        {SidebarContent}
      </aside>

      {/* Mobile overlay + drawer (below lg) */}
      {navOpen && (
        <button
          className="lg:hidden fixed inset-0 bg-black/60 z-[10001] backdrop-blur-sm"
          aria-label="Close navigation overlay"
          onClick={() => setNavOpen(false)}
          data-testid="mobile-nav-overlay"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-background border-r hairline flex flex-col z-[10002] transition-transform duration-200 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-nav-drawer"
      >
        {SidebarContent}
      </aside>

      <main className="flex-1 overflow-x-auto w-full min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b hairline flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => setNavOpen(true)}
            className="text-foreground p-1 -ml-1"
            aria-label="Open navigation"
            data-testid="mobile-nav-toggle"
          >
            <List size={24} weight="bold" />
          </button>
          <Link to="/app" className="flex items-center gap-2">
            <div className="h-7 w-7 border hairline flex items-center justify-center">
              <span className="font-serif text-lg leading-none">E</span>
            </div>
            <span className="font-serif text-xl tracking-tight">Estima</span>
          </Link>
          <div className="ml-auto inline-flex items-center gap-2">
            <ThemeToggle testid="mobile-theme-toggle" />
            {user?.is_pro && <Crown size={14} weight="duotone" className="text-[hsl(var(--secondary))]" />}
          </div>
        </div>

        {showTrialBanner && !hideBannerOnAdmin && (
          <div className="bg-[hsl(var(--muted))] border-b hairline px-4 sm:px-8 py-3 flex items-center justify-between gap-3 text-xs sm:text-sm" data-testid="trial-banner">
            <div className="flex items-center gap-2 min-w-0">
              <Lightning size={14} weight="duotone" className="text-[hsl(var(--secondary))] shrink-0" />
              <span className="truncate">
                <span className="text-[hsl(var(--secondary))]">
                  {user?.plan === "pro" ? "Pro trial active" : "Free trial active"}
                </span>
                {" · "}
                <span className="text-muted-foreground">
                  {trialLeft} day{trialLeft === 1 ? "" : "s"} remaining.
                  {user?.plan === "pro" ? " Every feature is yours." : " Basic features unlocked — upgrade anytime."}
                </span>
              </span>
            </div>
            <Link to="/app/profile" className="text-[hsl(var(--secondary))] hover:text-foreground text-xs inline-flex items-center gap-1 shrink-0" data-testid="trial-banner-upgrade">
              {user?.plan === "pro" ? "Lock it in" : "Upgrade"} <span aria-hidden>→</span>
            </Link>
          </div>
        )}
        {showExpiredBanner && !hideBannerOnAdmin && (
          <div className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--primary))] px-4 sm:px-8 py-3 flex items-center justify-between gap-3 text-xs sm:text-sm" data-testid="expired-banner">
            <div className="flex items-center gap-2 min-w-0">
              <Crown size={14} weight="duotone" className="text-[hsl(var(--primary))] shrink-0" />
              <span className="truncate">
                <span className="text-[hsl(var(--primary))]">Pro expired.</span>{" "}
                <span className="text-muted-foreground">AI Advisor and exports are locked.</span>
              </span>
            </div>
            <Link to="/app/profile" className="text-[hsl(var(--primary))] hover:text-foreground text-xs inline-flex items-center gap-1 shrink-0" data-testid="expired-banner-upgrade">
              Upgrade <span aria-hidden>→</span>
            </Link>
          </div>
        )}
        <Outlet />
        {/* App-shell footer with copyright + legal links (desktop only — bottom nav crowds mobile). */}
        <footer className="hidden lg:flex border-t hairline mt-12 px-8 py-6 text-[11px] text-muted-foreground items-center justify-between" data-testid="appshell-copyright">
          <div>© {new Date().getFullYear()} Estima — a personal property decision engine. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/disclaimer" className="hover:text-foreground">Disclaimer</Link>
          </div>
        </footer>
        {/* Bottom-tab-bar safe-area spacer on mobile */}
        <div className="lg:hidden h-20" aria-hidden />
      </main>

      {/* Bottom tab bar — mobile only */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-[10000] bg-background/95 backdrop-blur-md border-t hairline flex justify-around items-stretch pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-tabs"
      >
        {bottomTabs.map((t) => {
          const Icon = t.icon;
          if (t.isMore) {
            return (
              <button
                key="more"
                onClick={() => setNavOpen(true)}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-muted-foreground hover:text-foreground active:bg-[hsl(var(--muted))] transition"
                data-testid="bottom-tab-more"
              >
                <Icon size={20} weight="duotone" />
                <span className="text-[10px] leading-none">More</span>
              </button>
            );
          }
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`bottom-tab-${t.id}`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition ${
                  isActive
                    ? "text-[hsl(var(--primary))]"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={20} weight={isActive ? "fill" : "duotone"} />
                  <span className="text-[10px] leading-none">{t.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Floating help / re-open tour */}
      <button
        onClick={() => setShowTour(true)}
        className="hidden lg:flex fixed bottom-5 right-24 z-40 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-[hsl(var(--primary))] text-white items-center justify-center shadow-lg hover:bg-[#d46c44] transition"
        title="Open tour"
        data-testid="help-fab"
      >
        <Question size={18} weight="bold" />
      </button>
      <FeedbackWidget />
    </div>
  );
}
